/* ============================================================
   Account dialogs: invitations, user scope, API keys, backup schedules,
   network / organization switching, image push, profile and two-factor
   (port of modals.js 756–867)
   ============================================================ */
import { useContext, useState, type ReactNode } from "react";
import { DB, act, api, can, fire, go, hooks, job, load, site, toast, type Any } from "../lib/store";
import { Copyable, closeMenu, copy } from "../ui/common";
import { archFits, archOf } from "../lib/arch";
import { Btn, F, FormCtx, Frame, Modal, useForm, type Form } from "../ui/modal";
import { reloadKeys } from "../pages/account";
import { boot } from "../App";

/* ---------- helpers ---------- */
type Opt = string | number | [Any, ReactNode];
/* like the old DOM select: when the stored value is not an option, the first option is what gets submitted */
function Sel(p: { k: string; label: ReactNode; opts: Opt[]; hint?: ReactNode }) {
  const f = useContext(FormCtx)!;
  const vals = p.opts.map((o) => (Array.isArray(o) ? o[0] : o));
  if (vals.length && !vals.some((v) => String(v) === String(f.d[p.k]))) f.d[p.k] = vals[0];
  return <F.Sel {...p} />;
}
/* Modal.simple with keyed form fields */
function FormDialog({ title, init, body, action, fn, wide }:
  { title: ReactNode; init: Any; body: (f: Form) => ReactNode; action?: string; fn?: (d: Any, f: Form) => Any; wide?: boolean }) {
  const f = useForm(init);
  return (
    <FormCtx.Provider value={f}>
      <Frame title={title} wide={wide} footer={<>
        <div className="spacer" />
        <button className="btn" onClick={Modal.close}>Cancel</button>
        {action && fn ? <Btn primary onClick={() => fn(f.d, f)}>{action}</Btn> : null}
      </>}>{body(f)}</Frame>
    </FormCtx.Provider>
  );
}
const formModal = (p: Parameters<typeof FormDialog>[0]) => Modal.open(<FormDialog {...p} />);
const scopeOpts = (): string[] => ["All sites", ...DB.sites.map((s: Any) => "Site: " + s.name),
  ...[...new Set<string>(DB.workloads.flatMap((w: Any) => w.tags || []))].map((t) => "Tag: " + t)];

/* ---- invitations ---- */
export function invite() {
  formModal({
    title: "Invite someone",
    init: { email: "", role: "Operator", scope: "All sites", mfa: true, pa: false },
    body: () => (
      <>
        <F.Text k="email" label="Email address" />
        <Sel k="role" label="Role" opts={["Admin", "Operator", "Viewer"].concat(DB.me.role === "Owner" ? ["Owner"] : [])} />
        <Sel k="scope" label="Can reach" opts={scopeOpts()} />
        <F.Check k="mfa" title="Require two-factor authentication" sub="" />
        {DB.me.platform_admin ? <F.Check k="pa" title="Platform admin" sub="Can see and manage every organization, approve images and change platform settings. Give this to your own staff only." /> : null}
        <p className="hint">They join <b>{DB.org.org || ""}</b>{DB.me.platform_admin ? " — switch organization first to invite someone into another one" : ""}.</p>
      </>
    ),
    action: "Send invitation",
    fn: (d) => sendInvite(d),
  });
}
async function sendInvite(d: Any) {
  try {
    const r = await api("POST", "/api/users/invite", { email: d.email, role: d.role, scope: d.scope, mfa: d.mfa, platform_admin: !!d.pa });
    await load();
    inviteLink(r);
  } catch (e: Any) { toast("Not invited", e.message, "err"); }
}
export function inviteLink(r: Any) {
  Modal.open(
    <Frame title="Invitation created" footer={<>
      <div className="spacer" />
      <button className="btn" onClick={() => copy(r.link)}>Copy link</button>
      <button className="btn primary" onClick={Modal.close}>Done</button>
    </>}>
      <p style={{ marginTop: 0 }}>{r.emailed ? "An email is on its way. " : "No mail server is configured, so send this link yourself. "}It works for 7 days.</p>
      <pre className="log copy" style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }} onClick={() => copy(r.link)} title="Click to copy">{r.link}</pre>
    </Frame>,
  );
}
export function userScope(id: Any) {
  const u = DB.users.find((x: Any) => x.id === id);
  if (!u) return;
  formModal({
    title: `Access for ${u.name}`,
    init: { scope: u.scope, mfa: u.mfa_required },
    body: () => (
      <>
        <Sel k="scope" label="Can reach" opts={scopeOpts()} />
        <F.Check k="mfa" title="Require two-factor authentication" sub="" />
      </>
    ),
    action: "Save",
    fn: (d) => act("PUT", `/api/users/${u.id}`, { scope: d.scope, mfa_required: d.mfa }, "Access updated").then(() => Modal.close()),
  });
}

/* ---- API keys ---- */
export function newKey() {
  formModal({
    title: "Create API key",
    init: { name: "CI pipeline", scopes: "deploy,read" },
    body: () => (
      <>
        <F.Text k="name" label="Name" />
        <Sel k="scopes" label="Scopes" opts={[["read", "read — GET only"], ["deploy,read", "deploy, read — Operator actions"], ["admin,deploy,read", "admin — everything your role allows"]]} />
        <p className="hint">The key acts as you ({DB.me.role}), limited to the scopes above.</p>
      </>
    ),
    action: "Create",
    fn: (d) => createKey(d),
  });
}
async function createKey(d: Any) {
  try {
    const r = await api("POST", "/api/keys", { name: d.name, scopes: d.scopes });
    Modal.showKey(r.key, undefined, reloadKeys);
    reloadKeys();
  } catch (e: Any) { toast("Failed", e.message, "err"); }
}

/* ---- backup schedules ---- */
export function addBackup(target?: string) {
  const b = target && target.startsWith("w:") ? DB.backups.find((x: Any) => String(x.workload) === target.slice(2)) : null;
  const stores: string[] = [...new Set<string>(DB.nodes.flatMap((n: Any) => (n.storage || []).filter((s: Any) => s.active && s.content.includes("backup")).map((s: Any) => s.id)))];
  const targets = DB.workloads.filter((w: Any) => !w.external).map((w: Any) => ["w:" + w.id, w.name])
    .concat(DB.nodes.map((n: Any) => ["n:" + n.id, `All guests on ${n.name}`]));
  formModal({
    title: b ? "Edit backup schedule" : "New backup schedule",
    init: {
      target,
      when: b ? b.schedule : "Daily 01:00",
      dest: b ? b.dest : stores[0],
      keep: b ? b.keep.replace("keep-daily=", "").replace(",keep-weekly=", " daily, ") + " weekly" : "7 daily, 4 weekly",
      verify: b ? b.verify : true,
    },
    body: () => (
      <>
        <Sel k="target" label="What to back up" opts={targets} />
        <Sel k="when" label="How often" opts={["Daily 01:00", "Daily 02:00", "Daily 03:00", "Weekly Sunday 03:00", "Every 6 hours"]} />
        <Sel k="dest" label="Where to (Proxmox storage)" opts={stores.length ? stores : ["local"]} />
        <Sel k="keep" label="Keep" opts={["7 daily, 4 weekly", "14 daily, 8 weekly", "30 daily"]} />
        <F.Check k="verify" title="Test the restore once a month" sub="Restores into a scratch guest with its network unplugged, checks it boots, then deletes it." />
      </>
    ),
    action: b ? "Save schedule" : "Create schedule",
    fn: (d) => act("POST", "/api/backups", { target: d.target, when: d.when, dest: d.dest, keep: d.keep, verify: d.verify }, "Backup schedule saved").then(() => Modal.close()),
  });
}

/* ---- network / organization picker ---- */
function NetworkPicker() {
  const [netname, setNetname] = useState("");
  const row = (n: Any) => (
    <div key={n.id} className={"check " + (n.id === DB.org.network_id ? "on" : "")}
      onClick={() => fire(act("POST", `/api/networks/${n.id}/switch`, undefined, "Switched network", n.name).then(() => { Modal.close(); go("home"); }))}>
      <div><b>{n.name}</b><small>{n.id === DB.org.network_id ? "currently open · " : ""}{n.sites} sites · {n.devices} devices</small></div>
    </div>
  );
  const orgIds: Any[] = [...new Set(DB.networks.map((n: Any) => n.org_id))];
  const grouped = orgIds.length > 1 || DB.me.platform_admin;
  return (
    <Frame title={DB.me.platform_admin ? "Switch organization or network" : "Switch network"}
      footer={<><div className="spacer" /><button className="btn" onClick={Modal.close}>Cancel</button></>}>
      {grouped
        ? orgIds.map((o) => {
          const nets = DB.networks.filter((n: Any) => n.org_id === o);
          return (
            <div key={String(o)}>
              <div className="section-title" style={{ marginTop: 6 }}>{nets[0].org}{o === DB.org.org_id ? <> <span className="badge acc">current</span></> : null}</div>
              {nets.map(row)}
            </div>
          );
        })
        : DB.networks.map(row)}
      {can("Admin")
        ? <>
          <div className="section-title">New network in {DB.org.org || "this organization"}</div>
          <div style={{ display: "flex", gap: 8 }}>
            <input type="text" value={netname} onChange={(e) => setNetname(e.target.value)} placeholder="e.g. Retail North"
              style={{ flex: 1, padding: "7px 10px", border: "1px solid var(--line)", borderRadius: 6 }} />
            <button className="btn" onClick={() => fire(act("POST", "/api/networks", { name: netname }, "Network created").then(() => { Modal.close(); go("home"); }))}>Create</button>
          </div>
        </> : null}
      {DB.me.platform_admin
        ? <div className="btnrow" style={{ marginTop: 14 }}><button className="btn ghost" onClick={() => { Modal.close(); go("org"); }}>Manage organizations →</button></div> : null}
    </Frame>
  );
}
export function networkPicker() { Modal.open(<NetworkPicker />); }

/* ---- organizations ---- */
export function newOrg() {
  formModal({
    title: "New organization",
    init: { name: "", network: "", site: "Main site", owner: "" },
    body: () => (
      <>
        <p style={{ marginTop: 0, color: "var(--ink-2)" }}>A customer or business unit with its own devices, users, catalog, policy and audit log. People in it only ever see their own organization.</p>
        <F.Text k="name" label="Organization name" ph="Acme Retail" />
        <div className="grid g2"><F.Text k="network" label="First network" hint="Defaults to “<name> network”." /><F.Text k="site" label="First site" /></div>
        <F.Text k="owner" label="Invite an owner (optional)" hint="They get an Owner invitation for this organization only." ph="owner@customer.com" />
      </>
    ),
    action: "Create organization",
    fn: (d) => createOrg(d),
  });
}
async function createOrg(d: Any) {
  try {
    const r = await api("POST", "/api/orgs", { name: d.name, network: d.network, site: d.site, owner_email: d.owner });
    await load(); toast("Organization created", "You are now looking at it");
    go("org");
    if (r.invite) inviteLink(r.invite); else Modal.close();
  } catch (e: Any) { toast("Not created", e.message, "err"); }
}
export function renameOrg(id: Any) {
  const o = DB.orgs?.find((x: Any) => x.id === id);
  if (!o) return;
  formModal({
    title: `Rename ${o.name}`,
    init: { name: o.name },
    body: () => <F.Text k="name" label="Organization name" />,
    action: "Save",
    fn: (d) => act("PUT", `/api/orgs/${id}`, { name: d.name }, "Renamed").then(() => Modal.close()),
  });
}
function DeleteOrg({ o }: { o: Any }) {
  const [v, setV] = useState("");
  return (
    <Frame title={`Delete ${o.name}?`} footer={<>
      <div className="spacer" />
      <button className="btn" onClick={Modal.close}>Cancel</button>
      <Btn danger id="delbtn" disabled={v.trim() !== o.name}
        onClick={() => act("DELETE", `/api/orgs/${o.id}`, undefined, "Organization deleted").then(() => Modal.close())}>Delete organization</Btn>
    </>}>
      <p style={{ marginTop: 0 }}>This removes the organization, its networks, sites and <b>{o.users} user account(s)</b>. It is refused while it still has devices.</p>
      <label className="f"><span>Type <Copyable v={o.name} cls="strong" /> to confirm</span>
        <input type="text" id="delname" value={v} onChange={(e) => setV(e.target.value)} /></label>
    </Frame>
  );
}
export function deleteOrg(id: Any) {
  const o = DB.orgs?.find((x: Any) => x.id === id);
  if (!o) return;
  Modal.open(<DeleteOrg o={o} />);
}
export function switchOrg(id: Any) {
  const o = DB.orgs?.find((x: Any) => x.id === id);
  if (!o) return;
  const n = (o.networks || [])[0];
  if (!n) { toast("No network in this organization", "", "warn"); return; }
  fire(act("POST", `/api/networks/${n.id}/switch`, undefined, "Switched to " + o.name, n.name).then(() => go("home")));
}

/* ---- push an image to chosen devices ---- */
function PushImage({ i }: { i: Any }) {
  const have = new Set((i.cached_on || []).map(String));
  // devices of another CPU architecture cannot use the image: treated like "already has it"
  const wrongArch = (n: Any) => !archFits(i.arch, n.arch);
  const skip = (n: Any) => have.has(String(n.id)) || wrongArch(n);
  const missing = DB.nodes.filter((n: Any) => !skip(n));
  const [all, setAll] = useState(true);
  const [picked, setPicked] = useState<Set<string>>(() => new Set(missing.map((n: Any) => String(n.id))));
  const setOne = (id: string, on: boolean) => {
    setPicked((p) => { const s = new Set(p); on ? s.add(id) : s.delete(id); return s; });
    setAll(false);
  };
  const toggleAll = (on: boolean) => {
    setAll(on);
    setPicked(on ? new Set(DB.nodes.filter((n: Any) => !skip(n)).map((n: Any) => String(n.id))) : new Set());
  };
  const push = () => {
    const ids = DB.nodes.filter((n: Any) => !skip(n) && picked.has(String(n.id))).map((n: Any) => +n.id);
    if (!all && !ids.length) { toast("Pick at least one device", "", "warn"); return; }
    return job("POST", all ? `/api/images/${i.id}/push` : `/api/images/${i.id}/push-to`, all ? undefined : { node_ids: ids });
  };
  return (
    <Frame title={`Push ${i.name}`} footer={<>
      <div className="spacer" />
      <button className="btn" onClick={Modal.close}>Cancel</button>
      <Btn primary onClick={push}>Push</Btn>
    </>}>
      <p style={{ marginTop: 0, color: "var(--ink-2)" }}>Each device downloads the file once into its own storage, so later deployments do not wait on the site uplink.</p>
      <label className={"check " + (all ? "on" : "")}>
        <input type="checkbox" id="pushall" checked={all} onChange={(e) => toggleAll(e.target.checked)} />
        <span><b>All {i.arch ? archOf(i.arch) + " " : ""}devices that do not have it yet ({missing.length})</b><small>Untick to pick devices yourself.</small></span></label>
      <div style={{ maxHeight: 320, overflow: "auto", marginTop: 6 }}>
        {DB.nodes.map((n: Any) => {
          const h = skip(n), wa = !have.has(String(n.id)) && wrongArch(n);
          const on = !h && picked.has(String(n.id));
          return (
            <label key={n.id} className={"check " + (on ? "on" : "")} style={h ? { opacity: 0.55 } : undefined}>
              <input type="checkbox" className="pushn" value={n.id} disabled={h} checked={on} onChange={(e) => setOne(String(n.id), e.target.checked)} />
              <span><b>{n.name}</b><small>{site(n.site).name} · {n.status}{n.arch ? " · " + n.arch : ""}{wa ? ` · cannot run it (image is ${archOf(i.arch)})` : h ? " · already has it" : ""}</small></span></label>
          );
        })}
      </div>
    </Frame>
  );
}
export function pushImage(id: Any) {
  const i = DB.images.find((x: Any) => String(x.id) === String(id));
  if (!i) return;
  Modal.open(<PushImage i={i} />);
}

/* ---- profile / two-factor ---- */
function MfaSetup({ r, forced }: { r: Any; forced?: boolean }) {
  const f = useForm({ code: "" });
  return (
    <FormCtx.Provider value={f}>
      <Frame title="Set up two-factor authentication" footer={<>
        <div className="spacer" />
        {forced ? null : <button className="btn" onClick={Modal.close}>Cancel</button>}
        <Btn primary onClick={() => Profile.verify(f.d.code)}>Turn on</Btn>
      </>}>
        {forced ? <div className="banner"><span>△</span><div><b>Your organization requires two-factor</b>Finish this before continuing.</div></div> : null}
        <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "flex-start" }}>
          {/* the server sends an SVG document; shown as an image instead of injecting markup */}
          <div className="qr"><img alt="QR code" src={"data:image/svg+xml;charset=utf-8," + encodeURIComponent(r.qr || "")} style={{ width: 180, height: 180 }} /></div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <p style={{ marginTop: 0 }}>Scan the code with an authenticator app (Google Authenticator, 1Password, Authy…), or type this key:</p>
            <pre className="log copy" onClick={() => copy(r.secret)} title="Click to copy">{r.secret}</pre>
            <div className="btnrow" style={{ margin: "-4px 0 12px" }}><button className="btn sm" onClick={() => copy(r.secret)}>Copy key</button></div>
            <F.Text k="code" label="6-digit code from the app" />
          </div>
        </div>
      </Frame>
    </FormCtx.Provider>
  );
}

let mfaBusy = false;
export const Profile = {
  edit() {
    closeMenu();
    formModal({
      title: "Your profile",
      init: { name: DB.me.name, cur: "", new: "" },
      body: () => (
        <>
          <F.Text k="name" label="Name" />
          <div className="section-title">Change password</div>
          <F.Pass k="cur" label="Current password" />
          <F.Pass k="new" label="New password (10+ characters)" />
        </>
      ),
      action: "Save",
      fn: (d) => act("PUT", "/api/me", { name: d.name, current: d.cur || null, password: d.new || null }, "Profile saved").then(() => Modal.close()),
    });
  },
  async mfa(forced?: boolean) {
    closeMenu();
    if (mfaBusy) return;
    mfaBusy = true; setTimeout(() => { mfaBusy = false; }, 3000);
    if (DB.me.mfa && !forced) {
      formModal({
        title: "Two-factor authentication",
        init: { code: "" },
        body: () => (
          <>
            <p style={{ marginTop: 0 }}>Two-factor is on. To turn it off, enter a current code.</p>
            <F.Text k="code" label="Code from your app" />
          </>
        ),
        action: "Turn off",
        fn: (d) => act("POST", "/api/me/mfa/disable", { code: d.code }, "Two-factor turned off").then(() => Modal.close()),
      });
      return;
    }
    let r: Any;
    try { r = await api("POST", "/api/me/mfa/setup"); } catch (e: Any) { toast("Failed", e.message, "err"); return; }
    Modal.open(<MfaSetup r={r} forced={forced} />);
  },
  async verify(code: string) {
    try { await api("POST", "/api/me/mfa/verify", { code }); Modal.close(); toast("Two-factor is on"); await boot(); }
    catch (e: Any) { toast("Not turned on", e.message, "err"); }
  },
};
hooks.mfa = (f) => { fire(Profile.mfa(f)); };
